import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { rfpApi, rfqApi, arbSessionApi } from "@/lib/api-client";
import { toast } from "sonner";
import { ArrowLeft, Briefcase, Loader2, ScanSearch, FileSignature, Scale, Lock, LifeBuoy } from "lucide-react";
import { SovereignRFPCard } from "@/components/uc/SovereignRFPCard";
import { SovereignRFQCard } from "@/components/uc/SovereignRFQCard";
import { WinRateMonitor } from "@/components/uc/WinRateMonitor";
import { SessionVaultPanel } from "@/components/uc/SessionVaultPanel";

const RFP_FILTERS = ["pending", "founder_signed", "all"];
const RFQ_FILTERS = ["pending", "executed", "all"];

export default function ArbitrageBoardroom() {
  const [tab, setTab] = useState("rfp");
  const [rfps, setRfps] = useState([]);
  const [rfqs, setRfqs] = useState([]);
  const [rfpFilter, setRfpFilter] = useState("pending");
  const [rfqFilter, setRfqFilter] = useState("pending");
  const [scanning, setScanning] = useState(false);
  const [recovering, setRecovering] = useState(false);
  const [winKey, setWinKey] = useState(0);

  const loadRfps = useCallback(() => {
    rfpApi.list(rfpFilter).then((r) => setRfps(r.rfps || [])).catch(() => {});
  }, [rfpFilter]);
  const loadRfqs = useCallback(() => {
    rfqApi.list(rfqFilter).then((r) => setRfqs(r.rfqs || [])).catch(() => {});
  }, [rfqFilter]);

  useEffect(() => { loadRfps(); }, [loadRfps]);
  useEffect(() => { loadRfqs(); }, [loadRfqs]);

  const runScan = async () => {
    setScanning(true);
    toast.info("FEED SCAN — authenticated pass over your job feeds using Vault sessions…");
    try {
      const r = await arbSessionApi.scan();
      const line = (r.report || []).map((x) => `${x.platform}: ${x.status}`).join(" · ");
      const raised = (r.rfps_raised || []).length;
      raised
        ? toast.success(`FEED SCAN — ${line} · ${raised} RFP(s) raised for Founder review`)
        : toast.info(`FEED SCAN — ${line || "no sessions filed"} · 0 RFPs raised`);
      loadRfps();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Feed scan failed");
    } finally { setScanning(false); }
  };

  const onSigned = () => { loadRfps(); loadRfqs(); setWinKey((k) => k + 1); };

  const runRecovery = async () => {
    setRecovering(true);
    toast.info("VENTURE RECOVERY — logging into GoDaddy, scraping outstanding debt for BoyMenDon'tCry…");
    try {
      const r = await arbSessionApi.godaddyRecovery("BoyMenDon'tCry");
      if (!r.ok) { toast.error(r.detail || "GoDaddy session unusable — refile cookies"); return; }
      if (r.auto_settled?.ok) toast.success(`RECOVERED — $${r.debt_usd} auto-settled from wallet profit (≤$100 agency)`);
      else if (r.rfq) toast.success(`Debt $${r.debt_usd} found — Bailout RFQ raised for your sign-off (RFQ tab)`);
      else toast.info(`GoDaddy scanned — no outstanding debt detected ($${r.debt_usd})`);
      loadRfqs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Recovery failed");
    } finally { setRecovering(false); }
  };

  return (
    <div className="min-h-screen uc-grain relative" data-testid="arbitrage-boardroom">
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-6">
        <header className="flex items-center gap-4 flex-wrap mb-6">
          <Link to="/" data-testid="arb-back-link"
                className="uc-chip flex items-center gap-1.5 hover:bg-white/5"><ArrowLeft size={11}/> Dashboard</Link>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 border border-amber-500/40 bg-amber-500/10 flex items-center justify-center">
              <Briefcase size={15} className="text-amber-400" />
            </div>
            <div>
              <div className="font-mono-uc text-[9px] uppercase tracking-[0.3em] text-amber-400/80">SOVEREIGN ARBITRAGE PIPELINE</div>
              <h1 className="font-serif-display text-xl leading-none">Arbitrage Boardroom</h1>
            </div>
          </div>
          <div className="ml-auto flex items-center gap-2 flex-wrap">
            <span className="uc-chip flex items-center gap-1.5 text-white/50" data-testid="sequence-lock-chip">
              <Lock size={9} className="text-amber-400" /> Sequence Lock: RFP Founder-Sign → RFQ Authorize → Submission Seal
            </span>
            <button data-testid="arb-recovery-btn" onClick={runRecovery} disabled={recovering}
                    title="GoDaddy Venture Recovery — scrape BoyMenDon'tCry outstanding debt and raise a bailout"
                    className="uc-chip flex items-center gap-1.5 hover:border-rose-500/60 hover:bg-rose-500/10 transition-colors text-rose-300 disabled:opacity-50">
              {recovering ? <Loader2 size={10} className="animate-spin"/> : <LifeBuoy size={10}/>} Venture Recovery
            </button>
            <button data-testid="arb-scan-btn" onClick={runScan} disabled={scanning}
                    className="uc-chip flex items-center gap-1.5 hover:border-amber-500/60 hover:bg-amber-500/10 transition-colors text-amber-400 disabled:opacity-50">
              {scanning ? <Loader2 size={10} className="animate-spin"/> : <ScanSearch size={10}/>} Run Feed Scan
            </button>
          </div>
        </header>

        <SessionVaultPanel onChanged={loadRfps} />

        {/* Clinically separate tabs */}
        <div className="flex items-center gap-1 mb-4 border-b border-white/10" data-testid="arb-tabs">
          <button data-testid="arb-tab-rfp" onClick={() => setTab("rfp")}
                  className={`px-4 py-2 font-mono-uc text-[10px] uppercase tracking-widest flex items-center gap-1.5 border-b-2 -mb-px transition-colors ${
                    tab === "rfp" ? "border-sky-400 text-sky-300" : "border-transparent text-white/40 hover:text-white/70"}`}>
            <FileSignature size={11}/> RFP · Request for Proposal
          </button>
          <button data-testid="arb-tab-rfq" onClick={() => setTab("rfq")}
                  className={`px-4 py-2 font-mono-uc text-[10px] uppercase tracking-widest flex items-center gap-1.5 border-b-2 -mb-px transition-colors ${
                    tab === "rfq" ? "border-amber-400 text-amber-300" : "border-transparent text-white/40 hover:text-white/70"}`}>
            <Scale size={11}/> RFQ · Request for Quotation
          </button>
        </div>

        {tab === "rfp" && (
          <div data-testid="arb-rfp-panel">
            <WinRateMonitor refreshKey={winKey} />
            <div className="flex items-center gap-2 mb-3">
              {RFP_FILTERS.map((f) => (
                <button key={f} data-testid={`rfp-filter-${f}`} onClick={() => setRfpFilter(f)}
                        className={`uc-chip transition-colors ${rfpFilter === f ? "border-sky-500/60 text-sky-300 bg-sky-500/10" : "hover:bg-white/5"}`}>
                  {f.replace("_", " ")}
                </button>
              ))}
              <span className="ml-auto font-mono-uc text-[9px] text-white/35" data-testid="rfp-count">{rfps.length} RFP(s)</span>
            </div>
            <div className="space-y-3" data-testid="rfp-list">
              {rfps.map((r) => <SovereignRFPCard key={r.id} rfp={r} onSigned={onSigned}
                                                 onOutcome={() => setWinKey((k) => k + 1)} />)}
              {rfps.length === 0 && (
                <div className="border border-white/10 bg-black/40 px-4 py-6 text-center" data-testid="rfp-empty">
                  <div className="font-mono-uc text-[10px] uppercase tracking-widest text-white/40">
                    No {rfpFilter === "all" ? "" : rfpFilter.replace("_", " ") + " "}RFPs
                  </div>
                  <div className="font-mono-uc text-[9px] text-white/30 mt-1">
                    File a platform session above, then Run Feed Scan — high matches vs the $74.60 ROI profile raise RFPs here.
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {tab === "rfq" && (
          <div data-testid="arb-rfq-panel">
            <div className="flex items-center gap-2 mb-3">
              {RFQ_FILTERS.map((f) => (
                <button key={f} data-testid={`rfq-filter-${f}`} onClick={() => setRfqFilter(f)}
                        className={`uc-chip transition-colors ${rfqFilter === f ? "border-amber-500/60 text-amber-300 bg-amber-500/10" : "hover:bg-white/5"}`}>
                  {f}
                </button>
              ))}
              <span className="ml-auto font-mono-uc text-[9px] text-white/35" data-testid="rfq-count">{rfqs.length} RFQ(s)</span>
            </div>
            <div className="space-y-3" data-testid="rfq-list">
              {rfqs.map((q) => <SovereignRFQCard key={q.id} rfq={q} onExecuted={() => { loadRfqs(); loadRfps(); }} />)}
              {rfqs.length === 0 && (
                <div className="border border-white/10 bg-black/40 px-4 py-6 text-center" data-testid="rfq-empty">
                  <div className="font-mono-uc text-[10px] uppercase tracking-widest text-white/40">No {rfqFilter === "all" ? "" : rfqFilter + " "}RFQs</div>
                  <div className="font-mono-uc text-[9px] text-white/30 mt-1">
                    SEQUENCE LOCK — an RFQ only exists after its RFP is Founder-Signed in the RFP tab.
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
